import { useTheme } from "@emotion/react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  IconArrowLeft,
  IconCheck,
  IconDeviceFloppy,
  IconEye,
  IconInfoCircle,
  IconSearch,
} from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {
  DragAndDrop,
  Input,
  ProposalAverageConsumptionDialog,
  Select,
} from "~/components";
import * as yup from "yup";
import { useIndicationFlow, usePartner } from "~/contexts";
import {
  EConsumerUnitClassification,
  EConsumerUnitTension,
  EIndicationStatus,
  EProposalFileStatus,
  EProposalFileType,
  ETariffClassification,
  ProposalModel,
  ProposalMonthlyConsumption,
  useDealershipListQuery,
  useFormalizeIndicationProposalMutation,
  useGetProposalAndIndicationByIdQuery,
  useUpdateIndicationAddEnergyBillInfoMutation,
  useUpdateIndicationFilesMutation,
} from "~/graphql/types";
import { addressSchema, fileSchema } from "~/utils/yupSchema";
import { useCep, useToggle, useUploadMultiFilesWithNoProposal } from "~/hooks";
import { MultiFilesWithNoProposalReturn } from "~/services/uploadFile";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { documentFileIsDisabled } from "../utils";
import { UpdateDocWarning } from "../UpdateDocWarning";
import {
  customerTensionOptions,
  defaultOption,
  getConsumerClassificationOptions,
  Option,
  tariffClassificationOptions,
} from "~/utils/options";
import { brazilStates } from "~/constants";
import { DataReviewModal } from "./components/DataReviewModal";
import { clearString, onlyNumbers, transformMoney } from "~/utils";
import { monthsLabel } from "~/components/ProposalAverageConsumptionDialog";

type AddressForm = {
  city: string;
  state: string;
  street: string;
  number: string;
  zipcode: string;
  complement: string;
  neighborhood: string;
  country?: string;
};

export type DocumentationForm = {
  tusdValue?: number;
  tension: EConsumerUnitTension;
  energyTaxes?: number;
  AmountCents?: number;
  clientNumber: string;
  dealershipId: string;
  distributionTaxes?: number;
  avarageConsumption: string;
  installationNumber: string;
  invoiceDueDay?: number;
  tariffClassification: ETariffClassification;
  address: AddressForm;
  energyBill?: File[] | null;
  consumerClassification: EConsumerUnitClassification;
  customerPfDocumentWithPhotoBack?: File[] | null;
  customerPfDocumentWithPhotoFront?: File[] | null;
  customerPjDocumentCard?: File[] | null;
  customerPjSocialContract?: File[] | null;
  monthlyConsumption?: ProposalMonthlyConsumption;
};

type FormData = {
  customerPfDocumentWithPhotoFront: null | File[];
  customerPfDocumentWithPhotoBack: null | File[];
  customerPjSocialContract: null | File[];
  customerPjDocumentCard: null | File[];
  energyBill: null | File[];
  uploadedEnergyBill?: MultiFilesWithNoProposalReturn;
  address: AddressForm;
  tusdValue?: number;
  tension: EConsumerUnitTension;
  energyTaxes?: number;
  AmountCents?: number;
  clientNumber: string;
  distributionTaxes?: number;
  avarageConsumption: string;
  installationNumber: string;
  monthlyConsumption?: ProposalMonthlyConsumption;
  tariffClassification: ETariffClassification;
  dealershipId: string;
  invoiceDueDay?: number;
  consumerClassification: EConsumerUnitClassification;
};

const schema = yup
  .object({
    address: addressSchema,
    tusdValue: yup.number().required(),
    invoiceDueDay: yup.number().required().integer().min(1).max(31),
    consumerClassification: yup.string().required(),
    tension: yup.string().required(),
    energyTaxes: yup.number().required(),
    AmountCents: yup.number().required(),
    clientNumber: yup.string().required(),
    dealershipId: yup.string().required(),
    distributionTaxes: yup.number().required(),
    installationNumber: yup
      .string()
      .required()
      .matches(/^\d{15}$/, "Instalação / UC deve conter exatamente 15 dígitos"),
    avarageConsumption: yup.string().required(),
    tariffClassification: yup.string().required(),
    energyBill: fileSchema,
    customerPjDocumentCard: fileSchema,
    customerPjSocialContract: fileSchema,
    customerPfDocumentWithPhotoBack: fileSchema,
    customerPfDocumentWithPhotoFront: fileSchema,
    monthlyConsumption: yup.object(
      monthsLabel.reduce(
        (acc, month) => {
          acc[month] = yup
            .number()
            .transform((value, originalValue) => {
              return originalValue === "" ||
                typeof originalValue === "undefined"
                ? null
                : value;
            })
            .nullable()
            .notRequired()
            .integer()
            .moreThan(0);
          return acc;
        },
        {} as Record<string, any>,
      ),
    ),
  })
  .required();

export const Documents: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { fetchCep, isLoading } = useCep({ showErrorMessage: true });
  const { myPartner } = usePartner();
  const {
    selectedIndication,
    isPf,
    selectedProposal,
    setSelectedProposal,
    goToPage,
  } = useIndicationFlow();
  const [uploadMultiFiles, { loading: loadingFilesUpload }] =
    useUploadMultiFilesWithNoProposal();
  const [
    updateIndicationProposalAddEnergyBillInfo,
    { loading: loadingAddEnergyBillInfo },
  ] = useUpdateIndicationAddEnergyBillInfoMutation();
  const [
    formalizeIndicationProposal,
    { loading: formalizeIndicationProposalIsLoading },
  ] = useFormalizeIndicationProposalMutation({
    onError(error) {
      toast.error(error.message);
    },
    onCompleted(data) {
      if (
        setSelectedProposal &&
        data.formalizeIndicationProposal.termOfAdhesion
      ) {
        setSelectedProposal({
          ...selectedProposal,
          ...data.formalizeIndicationProposal,
        } as ProposalModel);
        // onNext()
      }
    },
  });
  const [filteredDealerships, setFilteredDealerships] = useState<Option[]>([
    defaultOption,
  ]);
  const [pollingFileTypes, setPollingFileTypes] = useState<EProposalFileType[]>(
    [],
  );
  const { data: dealerships, loading: loadingDealerships } =
    useDealershipListQuery({ variables: { params: { pageSize: 99999 } } });

  // Query para atualizar os dados quando entrar no step
  const { refetch: refetchProposalData } = useGetProposalAndIndicationByIdQuery(
    {
      variables: {
        id: selectedIndication?._id || "",
      },
      fetchPolicy: "network-only",
      skip: !selectedIndication?._id,
      onCompleted(data) {
        if (data.getIndicationRelatedEntitiesById.proposal) {
          setSelectedProposal(data.getIndicationRelatedEntitiesById.proposal);
        }
      },
    },
  );
  const formMethods = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      address: {
        city: "",
        state: "",
        street: "",
        number: "",
        zipcode: "",
        complement: "",
        neighborhood: "",
        country: "Brasil",
      },
      // ...outros campos se necessário
    },
  });
  const { isValid } = formMethods.formState;
  const { isTrue: isVisible, toggle: toggleModal } = useToggle();
  const {
    isTrue: contractDeactivationModalIsOpen,
    toggle: toggleContractDeactivation,
  } = useToggle();

  const address = formMethods.watch("address");
  const installationNumber = formMethods.watch("installationNumber");
  const isLegacyInstallationNumber = /^\d{16,}$/.test(installationNumber || "");
  const allowedStatusToUpdate = [
    EIndicationStatus.requestedProposal,
    EIndicationStatus.contractWithPendencies,
  ];
  const disabledFields = !allowedStatusToUpdate.includes(
    selectedIndication?.status as EIndicationStatus,
  );

  const onSearchCep = useCallback(async () => {
    if (address?.zipcode) {
      const { data } = await fetchCep(address?.zipcode);

      if (data) {
        formMethods.setValue("address.state", data.uf, { shouldDirty: true });
        formMethods.setValue("address.city", data.localidade);
        formMethods.setValue("address.street", data.logradouro);
        formMethods.setValue("address.neighborhood", data.bairro);
        formMethods.setValue("address.complement", data.complemento);
      }
    }
  }, [address?.zipcode]);

  const {
    customerPfDocumentWithPhotoBack: customerPfDocumentWithPhotoBackWatch,
    customerPfDocumentWithPhotoFront: customerPfDocumentWithPhotoFrontWatch,
    customerPjDocumentCard: customerPjDocumentCardWatch,
    customerPjSocialContract: customerPjSocialContractWatch,
    energyBill: energyBillWatch,
  } = useWatch({ control: formMethods.control });

  const enableSubmit = [
    customerPfDocumentWithPhotoBackWatch &&
      customerPfDocumentWithPhotoBackWatch?.length > 0,
    customerPfDocumentWithPhotoFrontWatch &&
      customerPfDocumentWithPhotoFrontWatch?.length > 0,
    customerPjSocialContractWatch && customerPjSocialContractWatch?.length > 0,
    customerPjDocumentCardWatch && customerPjDocumentCardWatch?.length > 0,
    energyBillWatch && energyBillWatch?.length > 0,
  ].some((item) => item);

  const [updateIndicationFiles, { loading: updateIndicationFilesIsLoading }] =
    useUpdateIndicationFilesMutation();

  const uploadAllFilesInIndication = useCallback(
    async (
      files: MultiFilesWithNoProposalReturn[] | null,
    ): Promise<boolean> => {
      if (!files || !(files?.length > 0)) return false;

      for (let index = 0; index < files.length; index++) {
        try {
          const result = await updateIndicationFiles({
            variables: {
              params: {
                id: selectedIndication?._id || "",
                type: files[index].type,
                file: {
                  fileName: files[index].file.fileName,
                  fileUrl: files[index].file.fileUrl,
                  key: files[index].file.key,
                  storageService: files[index].file.storageService,
                },
              },
            },
          });

          if (!result.data?.updateIndicationFiles) {
            toast.error("Erro ao salvar arquivo no banco de dados.");
            return false;
          }
        } catch (error: any) {
          toast.error(error.message || "Erro ao salvar arquivo");
          return false;
        }
      }

      return true;
    },
    [selectedIndication, updateIndicationFiles],
  );

  const energyBillFile = selectedProposal?.files?.find(
    (item) => item.type === EProposalFileType.energyBill,
  );
  const customerPfDocumentWithPhotoFront = selectedProposal?.files?.find(
    (item) => item.type === EProposalFileType.customerPfDocumentWithPhotoFront,
  );
  const customerPfDocumentWithPhotoBack = selectedProposal?.files?.find(
    (item) => item.type === EProposalFileType.customerPfDocumentWithPhotoBack,
  );
  const customerPjSocialContract = selectedProposal?.files?.find(
    (item) => item.type === EProposalFileType.customerPjSocialContract,
  );
  const customerPjDocumentCard = selectedProposal?.files?.find(
    (item) => item.type === EProposalFileType.customerPjDocumentCard,
  );

  useEffect(() => {
    const energyBillFileIsPendency = Boolean(energyBillFile?.rejectedAt);

    if (energyBillFileIsPendency) {
      formMethods.setError(`energyBill`, {
        message: "Este campo foi reprovado por um analista!",
      });
    }

    if (isPf) {
      const customerPfDocumentWithPhotoFrontIsPendency = Boolean(
        customerPfDocumentWithPhotoFront?.rejectedAt,
      );
      const customerPfDocumentWithPhotoBackIsPendency = Boolean(
        customerPfDocumentWithPhotoBack?.rejectedAt,
      );

      if (customerPfDocumentWithPhotoFrontIsPendency) {
        formMethods.setError(`customerPfDocumentWithPhotoFront`, {
          message: "Este campo foi reprovado por um analista!",
        });
      }

      if (customerPfDocumentWithPhotoBackIsPendency) {
        formMethods.setError(`customerPfDocumentWithPhotoBack`, {
          message: "Este campo foi reprovado por um analista!",
        });
      }
    } else {
      const customerPjSocialContractIsPendency = Boolean(
        customerPjSocialContract?.rejectedAt,
      );
      const customerPjDocumentCardIsPendency = Boolean(
        customerPjDocumentCard?.rejectedAt,
      );
      if (customerPjSocialContractIsPendency) {
        formMethods.setError(`customerPjSocialContract`, {
          message: "Este campo foi reprovado por um analista!",
        });
      }

      if (customerPjDocumentCardIsPendency) {
        formMethods.setError(`customerPjDocumentCard`, {
          message: "Este campo foi reprovado por um analista!",
        });
      }
    }
  }, []);

  // const getDealershipNameById = useCallback((id: string) => {
  //   const option = filteredDealerships.find(d => d.value === id)
  //   return option?.label || ''
  // }, [filteredDealerships])

  // useEffect para atualizar os dados quando entrar no step
  useEffect(() => {
    if (selectedIndication?._id) {
      refetchProposalData();
    }
  }, [selectedIndication?._id, refetchProposalData]);

  const uploadFiles = useCallback(
    async (
      data: FormData,
    ): Promise<{
      success: boolean;
      uploadedEnergyBill?: MultiFilesWithNoProposalReturn;
      uploadedFileTypes?: EProposalFileType[];
    }> => {
      try {
        const filesToUpload: File[][] = [];
        const fileTypes: EProposalFileType[] = [];

        if (data.energyBill && data.energyBill.length > 0) {
          filesToUpload.push(data.energyBill);
          fileTypes.push(EProposalFileType.energyBill);
        }

        if (
          data.customerPfDocumentWithPhotoFront &&
          data.customerPfDocumentWithPhotoFront.length > 0
        ) {
          filesToUpload.push(data.customerPfDocumentWithPhotoFront);
          fileTypes.push(EProposalFileType.customerPfDocumentWithPhotoFront);
        }

        if (
          data.customerPfDocumentWithPhotoBack &&
          data.customerPfDocumentWithPhotoBack.length > 0
        ) {
          filesToUpload.push(data.customerPfDocumentWithPhotoBack);
          fileTypes.push(EProposalFileType.customerPfDocumentWithPhotoBack);
        }

        if (
          data.customerPjSocialContract &&
          data.customerPjSocialContract.length > 0
        ) {
          filesToUpload.push(data.customerPjSocialContract);
          fileTypes.push(EProposalFileType.customerPjSocialContract);
        }

        if (
          data.customerPjDocumentCard &&
          data.customerPjDocumentCard.length > 0
        ) {
          filesToUpload.push(data.customerPjDocumentCard);
          fileTypes.push(EProposalFileType.customerPjDocumentCard);
        }

        // Sem arquivos novos para enviar
        if (filesToUpload.length === 0) {
          return { success: true, uploadedFileTypes: [] };
        }

        // 1. Upload para S3
        const uploadedFiles = await uploadMultiFiles({
          types: fileTypes,
          files: filesToUpload,
        });

        if (!uploadedFiles) {
          return { success: false };
        }

        // 2. Salva referências no banco via updateIndicationFiles
        const saveResult = await uploadAllFilesInIndication(uploadedFiles);
        if (!saveResult) {
          return { success: false };
        }

        // 3. Encontra o arquivo de energia (se houver) para passar ao uploadData
        const uploadedEnergyBill = uploadedFiles.find(
          (file) => file.type === EProposalFileType.energyBill,
        );

        return {
          success: true,
          uploadedEnergyBill,
          uploadedFileTypes: fileTypes,
        };
      } catch (error) {
        return { success: false };
      }
    },
    [uploadMultiFiles, uploadAllFilesInIndication],
  );

  /**
   * Faz polling na query GetProposalAndIndicationById para verificar se os
   * arquivos que eram "rejected" já foram atualizados pelo backend.
   * Tenta até MAX_ATTEMPTS vezes com intervalo de POLL_INTERVAL ms.
   */
  const pollForFileUpdate = useCallback(
    async (uploadedFileTypes: EProposalFileType[]): Promise<boolean> => {
      const MAX_ATTEMPTS = 15;
      const POLL_INTERVAL = 3000; // 3 segundos

      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));

        try {
          const { data } = await refetchProposalData();
          const proposal = data?.getIndicationRelatedEntitiesById?.proposal;

          if (proposal) {
            const files = proposal.files || [];

            // Verifica se TODOS os tipos de arquivo enviados não estão mais rejeitados
            const allFilesUpdated = uploadedFileTypes.every((type) => {
              const file = files.find((f) => f.type === type);
              return file && !file.rejectedAt;
            });

            if (allFilesUpdated) {
              return true;
            }
          }
        } catch (error) {
          console.error("Erro ao verificar atualização dos arquivos:", error);
        }
      }

      return false;
    },
    [refetchProposalData],
  );

  const uploadData = useCallback(
    async (data: FormData) => {
      try {
        // const dealershipNameResolved = getDealershipNameById(data.dealershipId)
        const result = await updateIndicationProposalAddEnergyBillInfo({
          variables: {
            params: {
              id: selectedIndication?._id || "",
              clientNumber: data.clientNumber,
              dealershipId: data.dealershipId,
              // dealershipName: dealershipNameResolved,
              installationNumber: data.installationNumber,
              tariffClassification: data.tariffClassification,
              address: {
                city: data.address.city,
                state: data.address.state,
                street: data.address.street,
                neighborhood: data.address.neighborhood,
                number: data.address.number,
                zipcode: clearString(data.address.zipcode),
                country: "Brasil",
                complement: data.address.complement || "",
              },
              tension: data.tension,
              tusdValue: data.tusdValue ? transformMoney(data.tusdValue) : 0,
              AmountCents: data.AmountCents
                ? transformMoney(data.AmountCents)
                : 0,
              energyTaxes: data.energyTaxes
                ? transformMoney(data.energyTaxes)
                : 0,
              avarageConsumption: Number(onlyNumbers(data.avarageConsumption)),
              distributionTaxes: data.distributionTaxes
                ? transformMoney(data.distributionTaxes)
                : 0,
              consumerClassification: data.consumerClassification,
              invoiceDueDay: data.invoiceDueDay,
              energyBillFile: data.uploadedEnergyBill
                ? {
                    file: {
                      fileUrl: data.uploadedEnergyBill.file.fileUrl,
                      fileName: data.uploadedEnergyBill.file.fileName,
                      key: data.uploadedEnergyBill.file.key,
                      storageService:
                        data.uploadedEnergyBill.file.storageService,
                    },
                    status: EProposalFileStatus.pending,
                    type: EProposalFileType.energyBill,
                  }
                : null,
            },
          },
        });
        return !!result.data;
      } catch (error) {
        return false;
      }
    },
    [selectedIndication, updateIndicationProposalAddEnergyBillInfo],
  );

  const formalizateProposal = useCallback(async () => {
    try {
      const result = await formalizeIndicationProposal({
        variables: {
          indicationId: selectedIndication?._id || "",
        },
      });
      return !!result.data;
    } catch (error) {
      return false;
    }
  }, [selectedIndication, formalizeIndicationProposal]);

  const handleSubmit = useCallback(async (data: FormData) => {
    try {
      const formalizationResult = await formalizateProposal();
      if (formalizationResult) {
        toast.success("Os documentos e dados foram enviados com sucesso!");
        goToPage(isPf ? 3 : 4);
      } else {
        toast.error(
          "Erro ao formalizar a proposta. Por favor, tente novamente.",
        );
      }
    } catch (error) {
      toast.error(
        "Ocorreu um erro ao enviar os documentos ou dados. Por favor, tente novamente.",
      );
    } finally {
      toggleModal();
    }
  }, []);

  const handleSaveData = useCallback(
    async (data: FormData) => {
      try {
        // 1. Upload dos arquivos (S3 + salvar referências no DB)
        const {
          success: filesSuccess,
          uploadedEnergyBill,
          uploadedFileTypes,
        } = await uploadFiles(data);
        if (!filesSuccess) {
          toast.error("Falha ao fazer upload dos arquivos");
          return;
        }

        // 2. Salva dados da fatura de energia
        const dataSuccess = await uploadData({ ...data, uploadedEnergyBill });
        if (!dataSuccess) {
          toast.error("Falha ao salvar os dados");
          return;
        }

        // 3. Limpa erros e faz polling para verificar se os arquivos atualizaram
        formMethods.clearErrors();
        toast.success("Dados salvos com sucesso!");

        if (uploadedFileTypes && uploadedFileTypes.length > 0) {
          toast.info("Aguardando atualização dos arquivos...");
          setPollingFileTypes(uploadedFileTypes);
          const filesUpdated = await pollForFileUpdate(uploadedFileTypes);
          setPollingFileTypes([]);

          if (filesUpdated) {
            toast.success("Arquivos atualizados com sucesso!");
          } else {
            toast.warn(
              "Os arquivos podem demorar para atualizar. Tente recarregar a página.",
            );
          }
        } else {
          // Sem arquivos novos, apenas recarrega os dados
          await refetchProposalData();
        }
      } catch (error) {
        toast.error(
          "Ocorreu um erro inesperado ao salvar os dados. Por favor, tente novamente.",
        );
      }
    },
    [
      uploadFiles,
      uploadData,
      pollForFileUpdate,
      refetchProposalData,
      formMethods,
    ],
  );

  const currentTariffClassification = formMethods.watch("tariffClassification");
  const consumerClassificationOptions = useMemo(
    () => getConsumerClassificationOptions(currentTariffClassification),
    [currentTariffClassification],
  );

  const isAddressCompleted = useMemo(() => {
    if (!selectedProposal?.customerRef?.address) return false;

    return !!(
      selectedProposal.customerRef.address.street &&
      selectedProposal.customerRef.address.number &&
      selectedProposal.customerRef.address.city &&
      selectedProposal.customerRef.address.state &&
      selectedProposal.customerRef.address.zipcode &&
      selectedProposal.customerRef.address.neighborhood
    );
  }, [selectedProposal?.customerRef?.address]);

  const isLegalRepresentativeCompleted = useMemo(() => {
    // Para PF, sempre retorna true (não precisa verificar representante legal)
    if (isPf) return true;

    // Para PJ, verifica se existe pelo menos um representante legal
    if (
      !selectedProposal?.customerRef?.legalRepresentative ||
      selectedProposal.customerRef.legalRepresentative.length === 0
    ) {
      return false;
    }

    // Verifica se todos os representantes legais têm os campos obrigatórios preenchidos
    return selectedProposal.customerRef.legalRepresentative.every(
      (representative) => {
        // Verifica campos básicos
        const hasBasicFields = !!(
          representative.name &&
          representative.document &&
          representative.identificationNumber &&
          representative.email &&
          representative.phone
        );

        // Verifica endereço do representante legal
        const hasAddress = !!(
          representative.address &&
          representative.address.street &&
          representative.address.number &&
          representative.address.city &&
          representative.address.state &&
          representative.address.zipcode &&
          representative.address.neighborhood
        );

        // Verifica documentos obrigatórios do representante legal (RG/CNH frente e verso)
        const hasRequiredDocuments = !!(
          representative.files &&
          representative.files.some(
            (file) =>
              file.type === "customerPjRepresentativeDocumentWithPhotoFront" &&
              file.status === "send",
          ) &&
          representative.files.some(
            (file) =>
              file.type === "customerPjRepresentativeDocumentWithPhotoBack" &&
              file.status === "send",
          )
        );

        return hasBasicFields && hasAddress && hasRequiredDocuments;
      },
    );
  }, [isPf, selectedProposal?.customerRef?.legalRepresentative]);

  const isFilesCompleted = useMemo(() => {
    // Verifica se o arquivo de energia está preenchido (obrigatório para todos)
    // Considera tanto arquivos já salvos quanto arquivos sendo enviados no momento
    const hasEnergyBill =
      !!(energyBillWatch && energyBillWatch.length > 0) ||
      !!(energyBillFile && energyBillFile.file);

    if (isPf) {
      // Para PF: verifica se os documentos de frente e verso estão preenchidos
      const hasFrontDoc =
        !!(
          customerPfDocumentWithPhotoFrontWatch &&
          customerPfDocumentWithPhotoFrontWatch.length > 0
        ) ||
        !!(
          customerPfDocumentWithPhotoFront &&
          customerPfDocumentWithPhotoFront.file
        );
      const hasBackDoc =
        !!(
          customerPfDocumentWithPhotoBackWatch &&
          customerPfDocumentWithPhotoBackWatch.length > 0
        ) ||
        !!(
          customerPfDocumentWithPhotoBack &&
          customerPfDocumentWithPhotoBack.file
        );

      return hasEnergyBill && hasFrontDoc && hasBackDoc;
    } else {
      // Para PJ: verifica se o contrato social e cartão CNPJ estão preenchidos
      const hasSocialContract =
        !!(
          customerPjSocialContractWatch &&
          customerPjSocialContractWatch.length > 0
        ) || !!(customerPjSocialContract && customerPjSocialContract.file);
      const hasDocumentCard =
        !!(
          customerPjDocumentCardWatch && customerPjDocumentCardWatch.length > 0
        ) || !!(customerPjDocumentCard && customerPjDocumentCard.file);

      return hasEnergyBill && hasSocialContract && hasDocumentCard;
    }
  }, [
    isPf,
    energyBillWatch,
    energyBillFile,
    customerPfDocumentWithPhotoFrontWatch,
    customerPfDocumentWithPhotoFront,
    customerPfDocumentWithPhotoBackWatch,
    customerPfDocumentWithPhotoBack,
    customerPjSocialContractWatch,
    customerPjSocialContract,
    customerPjDocumentCardWatch,
    customerPjDocumentCard,
  ]);

  useEffect(() => {
    if (dealerships && dealerships.dealershipList.data.length !== 0) {
      const mappedDealerships = dealerships.dealershipList.data.map((item) => {
        return {
          label: item.name || "",
          value: item._id,
        };
      });

      if (mappedDealerships.length !== 0) {
        const registeredDealershipId =
          selectedProposal?.energyBillInfo?.dealershipId;

        setFilteredDealerships(mappedDealerships);
        formMethods.setValue(
          "dealershipId",
          registeredDealershipId ?? mappedDealerships[0].value,
        );
      } else {
        formMethods.setValue("dealershipId", "");
        setFilteredDealerships([defaultOption]);
      }
    }

    if (selectedProposal) {
      const { energyBillInfo } = selectedProposal;

      if (energyBillInfo?.address) {
        formMethods.setValue("address.city", energyBillInfo.address.city);
        formMethods.setValue(
          "address.complement",
          energyBillInfo.address.complement || "",
        );
        formMethods.setValue(
          "address.neighborhood",
          energyBillInfo.address.neighborhood,
        );
        formMethods.setValue("address.number", energyBillInfo.address.number);
        formMethods.setValue("address.state", energyBillInfo.address.state);
        formMethods.setValue("address.street", energyBillInfo.address.street);
        formMethods.setValue("address.zipcode", energyBillInfo.address.zipcode);
      }
      formMethods.setValue(
        "monthlyConsumption",
        selectedProposal.monthlyConsumption || {},
      );
      formMethods.setValue(
        "tension",
        energyBillInfo?.tension || ("" as EConsumerUnitTension),
      );
      formMethods.setValue("dealershipId", energyBillInfo?.dealershipId || "");
      formMethods.setValue("clientNumber", energyBillInfo?.clientNumber || "");
      formMethods.setValue(
        "installationNumber",
        energyBillInfo?.installationNumber || "",
      );
      formMethods.setValue(
        "tariffClassification",
        energyBillInfo?.tariffClassification || ("" as ETariffClassification),
      );
      formMethods.setValue(
        "avarageConsumption",
        energyBillInfo?.avarageConsumption
          ? String(energyBillInfo?.avarageConsumption)
          : "",
      );
      formMethods.setValue(
        "tusdValue",
        energyBillInfo?.tusdValue
          ? transformMoney(energyBillInfo?.tusdValue, "toReal")
          : 0,
      );
      formMethods.setValue(
        "AmountCents",
        energyBillInfo?.AmountCents
          ? transformMoney(energyBillInfo?.AmountCents, "toReal")
          : 0,
      );
      formMethods.setValue(
        "energyTaxes",
        energyBillInfo?.energyTaxes
          ? transformMoney(energyBillInfo?.energyTaxes, "toReal")
          : 0,
      );
      formMethods.setValue(
        "distributionTaxes",
        energyBillInfo?.distributionTaxes
          ? transformMoney(energyBillInfo?.distributionTaxes, "toReal")
          : 0,
      );
      formMethods.setValue(
        "invoiceDueDay",
        energyBillInfo?.invoiceDueDay || undefined,
      );
      formMethods.setValue(
        "consumerClassification",
        energyBillInfo?.consumerClassification ||
          ("" as EConsumerUnitClassification),
      );
      // formMethods.setValue('energyBillFile', energyBillInfo?.energyBillFile || undefined)
    }
  }, [dealerships, formMethods, selectedProposal]);

  return (
    <>
      <FormProvider {...formMethods}>
        <form
        // onSubmit={formMethods.handleSubmit((data) => handleSubmit(data))}
        >
          {isPf && (
            <Box sx={{ display: "flex", gap: "24px", flexDirection: "column" }}>
              <Typography
                variant="h3"
                fontWeight={500}
                color={theme.palette.grey[800]}
              >
                Documento de identificação
              </Typography>
              <UpdateDocWarning />
              <Box sx={{ display: "flex", gap: 2, flexDirection: "row" }}>
                <DragAndDrop
                  $variant="secondary"
                  label="Anexar RG ou CNH (Frente)"
                  fileDescription="(JPG, JPEG, PNG, PDF. Tamanho máximo 1MB)"
                  name="customerPfDocumentWithPhotoFront"
                  disabled={
                    documentFileIsDisabled(customerPfDocumentWithPhotoFront) ||
                    disabledFields
                  }
                  status={
                    customerPfDocumentWithPhotoFront?.status as EProposalFileStatus
                  }
                  sentAt={customerPfDocumentWithPhotoFront?.sendAt}
                  $completed={customerPfDocumentWithPhotoFront?.sendAt}
                  fileName={customerPfDocumentWithPhotoFront?.file?.fileName}
                  fileUrl={customerPfDocumentWithPhotoFront?.file?.fileUrl}
                  isUploading={pollingFileTypes.includes(
                    EProposalFileType.customerPfDocumentWithPhotoFront,
                  )}
                  accepted={{
                    images: true,
                    pdf: true,
                  }}
                />

                <DragAndDrop
                  $variant="secondary"
                  label="Anexar RG ou CNH (Verso)"
                  fileDescription="(JPG, JPEG, PNG, PDF. Tamanho máximo 1MB)"
                  name="customerPfDocumentWithPhotoBack"
                  disabled={
                    documentFileIsDisabled(customerPfDocumentWithPhotoBack) ||
                    disabledFields
                  }
                  status={
                    customerPfDocumentWithPhotoBack?.status as EProposalFileStatus
                  }
                  sentAt={customerPfDocumentWithPhotoBack?.sendAt}
                  $completed={customerPfDocumentWithPhotoBack?.sendAt}
                  fileName={customerPfDocumentWithPhotoBack?.file?.fileName}
                  fileUrl={customerPfDocumentWithPhotoBack?.file?.fileUrl}
                  isUploading={pollingFileTypes.includes(
                    EProposalFileType.customerPfDocumentWithPhotoBack,
                  )}
                  accepted={{
                    images: true,
                    pdf: true,
                  }}
                />
              </Box>
            </Box>
          )}

          {!isPf && (
            <>
              <Typography
                sx={{ padding: "1.6rem 0 .6rem 0" }}
                variant="h3"
                fontWeight={500}
                color={theme.palette.grey[800]}
              >
                Contrato social
              </Typography>
              <DragAndDrop
                $variant="secondary"
                label="Anexar contrato social"
                fileDescription="(JPG, JPEG, PNG, PDF. Tamanho máximo 1MB)"
                name="customerPjSocialContract"
                disabled={
                  documentFileIsDisabled(customerPjSocialContract) ||
                  disabledFields
                }
                status={customerPjSocialContract?.status as EProposalFileStatus}
                sentAt={customerPjSocialContract?.sendAt}
                $completed={customerPjSocialContract?.sendAt}
                fileName={customerPjSocialContract?.file?.fileName}
                fileUrl={customerPjSocialContract?.file?.fileUrl}
                isUploading={pollingFileTypes.includes(
                  EProposalFileType.customerPjSocialContract,
                )}
              />

              <Typography
                sx={{ padding: "1.6rem 0 .6rem 0" }}
                variant="h3"
                fontWeight={500}
                color={theme.palette.grey[800]}
              >
                Cartão CNPJ
              </Typography>
              <DragAndDrop
                $variant="secondary"
                label="Anexar cartão CNPJ"
                fileDescription="(JPG, JPEG, PNG, PDF. Tamanho máximo 1MB)"
                name="customerPjDocumentCard"
                disabled={
                  documentFileIsDisabled(customerPjDocumentCard) ||
                  disabledFields
                }
                status={customerPjDocumentCard?.status as EProposalFileStatus}
                sentAt={customerPjDocumentCard?.sendAt}
                $completed={customerPjDocumentCard?.sendAt}
                fileName={customerPjDocumentCard?.file?.fileName}
                fileUrl={customerPjDocumentCard?.file?.fileUrl}
                isUploading={pollingFileTypes.includes(
                  EProposalFileType.customerPjDocumentCard,
                )}
              />
            </>
          )}
          <Box
            sx={{
              display: "flex",
              gap: "16px",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ padding: "1.6rem 0 .6rem 0" }}
              variant="h3"
              fontWeight={500}
              color={theme.palette.grey[800]}
            >
              Fatura de energia
            </Typography>
            <Typography
              sx={{ padding: "1.6rem 0 .6rem 0" }}
              variant="body1"
              fontWeight={400}
              color={"#999999"}
            >
              Informe os dados e anexe o arquivo da fatura de energia
            </Typography>
          </Box>

          <DragAndDrop
            $variant="secondary"
            label="Anexar fatura de energia"
            fileDescription="(JPG, JPEG, PNG, PDF. Tamanho máximo 1MB)"
            name="energyBill"
            disabled={documentFileIsDisabled(energyBillFile) || disabledFields}
            status={energyBillFile?.status as EProposalFileStatus}
            sentAt={energyBillFile?.sendAt}
            $completed={energyBillFile?.sendAt}
            fileName={energyBillFile?.file?.fileName}
            fileUrl={energyBillFile?.file?.fileUrl}
            isUploading={pollingFileTypes.includes(
              EProposalFileType.energyBill,
            )}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              padding: "1rem 0",
            }}
          >
            <Typography fontWeight={500} color={theme.palette.grey[800]}>
              Dados da fatura
            </Typography>

            <Grid spacing={3} container>
              <Grid item md={4} xs={12}>
                <Input
                  disabled={disabledFields}
                  mask="BRL"
                  name="AmountCents"
                  label="Valor da fatura"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Select
                  disabled={disabledFields}
                  options={tariffClassificationOptions}
                  name="tariffClassification"
                  label="Classificação tarifárica"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Select
                  disabled={disabledFields}
                  options={consumerClassificationOptions}
                  name="consumerClassification"
                  label="Classe de consumo"
                />
              </Grid>

              <Grid item md={4} xs={12}>
                <Select
                  disabled={disabledFields}
                  options={customerTensionOptions}
                  name="tension"
                  label="Tensão do consumidor"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Input
                  disabled={disabledFields}
                  showYupMessage={!disabledFields}
                  customMask={
                    isLegacyInstallationNumber
                      ? undefined
                      : "###############"
                  }
                  name="installationNumber"
                  label="Instalação / UC"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Input
                  disabled={disabledFields}
                  name="clientNumber"
                  label="Número do cliente"
                />
              </Grid>

              <Grid item md={4} xs={12}>
                <Box
                  onClick={toggleContractDeactivation}
                  sx={{
                    "& .MuiInputBase-root": {
                      paddingRight: "0",
                      pointerEvents: "none",
                    },
                    cursor: "pointer",
                  }}
                  role="button"
                  aria-disabled={disabledFields}
                >
                  <Input
                    disabled={disabledFields}
                    mask="kwh"
                    name="avarageConsumption"
                    label="Média de consumo"
                    inputProps={{ readOnly: true, tabIndex: -1 }}
                    icons={{
                      end: {
                        element: (
                          <IconButton
                            tabIndex={1}
                            sx={{ pointerEvents: "all" }}
                          >
                            <IconEye size={24} color="#3BA1FF" />
                          </IconButton>
                        ),
                      },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item md={4} xs={12}>
                <Input
                  disabled={loadingDealerships || disabledFields}
                  type="number"
                  inputProps={{ min: 1, max: 31 }}
                  name="invoiceDueDay"
                  label="Dia de vencimento"
                  icons={{
                    end: {
                      element: (
                        <Tooltip title="Data máxima para pagamento mensal de fatura. Ex.: Todo dia 05">
                          <IconInfoCircle size={24} color="#3BA1FF" />
                        </Tooltip>
                      ),
                    },
                  }}
                />
              </Grid>

              <Grid item md={4} xs={12}>
                <Select
                  disabled={loadingDealerships || disabledFields}
                  options={filteredDealerships}
                  name="dealershipId"
                  label="Concessionária"
                />
              </Grid>

              <Grid item md={4} xs={12}>
                <Input
                  disabled={disabledFields}
                  mask="BRL"
                  name="distributionTaxes"
                  label="Taxa de contribuição"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Input
                  disabled={disabledFields}
                  mask="BRL"
                  name="energyTaxes"
                  label="Taxa de energia"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Input
                  disabled={disabledFields}
                  mask="BRL"
                  name="tusdValue"
                  label="Tusd"
                />
              </Grid>
              {/* {!isMobile && <Grid item xs={8} />} */}
              <Grid item md={4} xs={12}>
                <Input
                  icons={{
                    end: {
                      element: isLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <IconButton onClick={() => onSearchCep()}>
                          <IconSearch />
                        </IconButton>
                      ),
                    },
                  }}
                  mask="cep"
                  disabled={disabledFields}
                  label="CEP"
                  name="address.zipcode"
                />
              </Grid>
              {!isMobile && <Grid item xs={8} />}
              <Grid item md={4} xs={12}>
                <Input
                  disabled={disabledFields}
                  name="address.street"
                  label="Rua"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Input
                  disabled={disabledFields}
                  name="address.number"
                  label="Número"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Input
                  disabled={disabledFields}
                  name="address.complement"
                  label="Complemento"
                />
              </Grid>

              <Grid item md={4} xs={12}>
                <Input
                  disabled={disabledFields}
                  name="address.neighborhood"
                  label="Bairro"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Input
                  disabled={disabledFields}
                  name="address.city"
                  label="Cidade"
                />
              </Grid>
              <Grid item md={4} xs={12}>
                <Select
                  disabled={disabledFields}
                  options={brazilStates}
                  name="address.state"
                  label="UF"
                />
              </Grid>
            </Grid>
          </Box>
          <Divider sx={{ margin: "2rem 0" }} />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Link to="/app/indications">
              <Button startIcon={<IconArrowLeft />} color="secondary">
                Voltar
              </Button>
            </Link>

            <Box sx={{ display: "flex", gap: "1.5rem", flexDirection: "row" }}>
              <Button
                onClick={formMethods.handleSubmit(handleSaveData, (errors) => {
                  toast.error(
                    "Preencha todos os campos obrigatórios antes de gravar.",
                  );
                })}
                type="button"
                color="secondary"
                disabled={
                  disabledFields ||
                  loadingFilesUpload ||
                  updateIndicationFilesIsLoading ||
                  loadingAddEnergyBillInfo
                }
                startIcon={
                  loadingFilesUpload ||
                  updateIndicationFilesIsLoading ||
                  loadingAddEnergyBillInfo ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <IconDeviceFloppy size={24} />
                  )
                }
              >
                Gravar dados
              </Button>
              <Button
                onClick={toggleModal}
                type="button"
                disabled={
                  selectedIndication?.status !== EIndicationStatus.requestedProposal ||
                  !isAddressCompleted ||
                  !isLegalRepresentativeCompleted ||
                  !isFilesCompleted ||
                  !isValid ||
                  loadingFilesUpload ||
                  updateIndicationFilesIsLoading ||
                  loadingAddEnergyBillInfo ||
                  formalizeIndicationProposalIsLoading ||
                  !myPartner?.allowFormalizeProposal
                }
              >
                Seguir para assinatura
              </Button>
            </Box>
          </Box>
        </form>

        <DataReviewModal
          isVisible={isVisible}
          toggleModal={toggleModal}
          onSubmit={formMethods.handleSubmit((data) => handleSubmit(data))}
          loading={
            loadingFilesUpload ||
            updateIndicationFilesIsLoading ||
            loadingAddEnergyBillInfo ||
            formalizeIndicationProposalIsLoading
          }
        />
        {contractDeactivationModalIsOpen && (
          <ProposalAverageConsumptionDialog
            isVisible={contractDeactivationModalIsOpen}
            toggleModal={toggleContractDeactivation}
          />
        )}
      </FormProvider>
    </>
  );
};
